import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import CustomButton from '../../components/common/CustomButton';
import { API_BASE_URL, AUTH_TOKEN_KEY } from '../../utils/constants';
import './OTPVerificationScreen.css';

const OTPVerificationScreen = () => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  const { token, mobile } = location.state || {};

  useEffect(() => {
    if (!token || !mobile) {
      navigate('/login');
      return;
    }

    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          setCanResend(true);
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [token, mobile, navigate]);

  const handleOtpChange = (index, value) => {
    // Handle paste event
    if (value.length > 1) {
      const pastedValue = value.replace(/\D/g, '').slice(0, 6);
      const newOtp = [...otp];
      
      for (let i = 0; i < 6; i++) {
        newOtp[i] = pastedValue[i] || '';
      }
      
      setOtp(newOtp);
      
      // Focus last filled input or last input
      const lastIndex = Math.min(pastedValue.length - 1, 5);
      const targetInput = document.getElementById(`otp-${lastIndex}`);
      if (targetInput) targetInput.focus();
      return;
    }
    
    // Only allow numeric values
    if (!/^\d*$/.test(value)) return;
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handleVerifyOTP = async () => {
    const otpString = otp.join('');
    
    if (otpString.length !== 6) {
      toast.error('Please enter complete OTP');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}api/auth/doctor/verify`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token,
          otpCode: otpString
        }),
      });

      const result = await response.json();

      if (response.ok) {
        toast.success(result.message);
        if (result.user) {
          localStorage.setItem('USER_DETAILS', JSON.stringify(result.user));
        }
        localStorage.setItem(AUTH_TOKEN_KEY, result.token);
        if (result.isProfilePending) {
          navigate('/additional-details');
        } else {
          navigate('/dashboard');
        }
      } else {
        toast.error(result.errorInfo || 'OTP verification failed');
      }
    } catch (error) {
      toast.error('An error occurred. Please try again.');
      console.error('OTP Verification Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (!canResend) return;

    setLoading(true);
    setTimer(60);
    setCanResend(false);

    try {
      const response = await fetch(`${API_BASE_URL}api/auth/doctor/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mobile
        }),
      });

      const result = await response.json();

      if (response.ok) {
        toast.success('OTP sent successfully!');
      } else {
        toast.error('Failed to resend OTP');
      }
    } catch (error) {
      toast.error('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="otp-container">
      <div className="otp-card">
        <div className="otp-header">
          <img src={require('../../assets/images/logo.jpg')} alt="Expert for Professionals" className="otp-logo" />
          <h1>Verify OTP</h1>
          <p>Enter the 6-digit code sent to +91 {mobile}</p>
        </div>

        <div className="otp-form">
          <div className="otp-inputs">
            {otp.map((digit, index) => (
              <input
                key={index}
                id={`otp-${index}`}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                className="otp-input"
                maxLength={6}
              />
            ))}
          </div>

          <CustomButton
            title={loading ? 'Verifying...' : 'Verify OTP'}
            onPress={handleVerifyOTP}
            disabled={loading}
            size="large"
          />

          <div className="resend-container">
            {canResend ? (
              <button 
                className="resend-button" 
                onClick={handleResendOTP}
                disabled={loading}
              >
                Resend OTP
              </button>
            ) : (
              <p>Resend OTP in {timer}s</p>
            )}
          </div>
        </div>

        <button 
          className="back-button"
          onClick={() => navigate('/login')}
        >
          ← Back to Login
        </button>
      </div>
    </div>
  );
};

export default OTPVerificationScreen;